import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OfferType,
  useCurrentOrganizationId,
  useOffers,
  usePlanOffers,
  useSendAction,
  useSession,
  useAddOfferToPlan,
  useUpdateOptionsInPlan,
  Product,
  SessionStatus,
  useIsAdmin,
} from '@app/api';
import { CenteredModal, SubmitButton } from '@app/core';
import { createUseStyles } from 'react-jss';
import { OfferOptionBlock, SupportOfferBlock } from '../Offers';
import { Theme } from '@app/theme';
import { filterOffers } from 'pages/StudySetup/GeneralSetup/filterOffers';
import { Form, Formik } from 'formik';
import { getSupportOfferPrice } from 'lib/planAndOffer';

interface Props {
  isOpen: boolean;
  sessionId?: string;
  onRequestClose: () => void;
}

interface Values {
  options: string[];
  supportOfferId: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  suportContainer: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 3 + 'px',
    marginBottom: theme.marginBase * 6,

    [theme.media.desktop]: {
      flexDirection: 'row',
    },
  },
  title: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 2,
  },
  optionsContainer: {},
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: theme.marginBase * 3,
  },
}));

export const UpsellStudyModal = ({
  isOpen,
  onRequestClose,
  sessionId,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const organizationId = useCurrentOrganizationId(true);

  const { data: allOffers } = useOffers();
  const { data: session } = useSession(sessionId);
  const { data: planOffers } = usePlanOffers(session?.planId);
  const { mutateAsync: updateSupportOffer } = useAddOfferToPlan(
    session?.planId,
  );
  const { mutateAsync: updateOptionsInPlan } = useUpdateOptionsInPlan(
    session?.planId,
  );
  const isAdmin = useIsAdmin();
  const { mutate: sendAction } = useSendAction('unlock_support_feature');

  const downsellAllowed = session?.status === SessionStatus.DRAFT;

  const alreadySelectedProducts = useMemo(() => {
    const targetingOffer = planOffers?.find(
      (o) => o.type === OfferType.TARGETING,
    );
    if (!targetingOffer) {
      return [];
    }
    return targetingOffer.products.map((p) => p.product) as Product[];
  }, [planOffers]);

  const availableOffers = useMemo(() => {
    if (!session || !allOffers) return [];
    return filterOffers(session, allOffers);
  }, [session, allOffers]);

  const initialValues = useMemo(() => {
    const res: Values = { options: [] as string[], supportOfferId: '' };
    planOffers?.forEach((o) => {
      if (o.type === OfferType.SUPPORT) {
        res.supportOfferId = o.id;
      }
      if (o.type === OfferType.OPTION) {
        res.options.push(o.id);
      }
    });
    return res;
  }, [planOffers]);

  const options = useMemo(
    () =>
      allOffers?.filter(
        (offer) => offer.type === OfferType.OPTION && !offer.requireCallToSales,
      ) || [],
    [allOffers],
  );

  let supportOffers = useMemo(
    () =>
      availableOffers?.filter(
        (o) => o.type === OfferType.SUPPORT && !o.requireCallToSales,
      ) || [],
    [availableOffers, planOffers],
  );

  const onSubmit = useCallback(
    async (values: Values) => {
      await updateSupportOffer({
        offerId: values.supportOfferId,
      });
      await updateOptionsInPlan({
        optionsIds: values.options,
      });
      const offer = supportOffers?.find((o) => o.id === values.supportOfferId);
      sendAction({
        offer,
        session,
      });
      onRequestClose();
    },
    [onRequestClose, updateSupportOffer, supportOffers],
  );

  const duration = session?.duration || 60;
  const disabledOffers = useMemo(() => {
    const res = [] as string[];
    if (isAdmin || downsellAllowed) {
      return res;
    }

    res.push(...initialValues.options);
    const supportOffer = planOffers?.find((o) => o.type === OfferType.SUPPORT);
    if (!supportOffer) {
      return res;
    }
    const price = getSupportOfferPrice(supportOffer, alreadySelectedProducts);
    for (const o of supportOffers) {
      const offerPrice = getSupportOfferPrice(o, alreadySelectedProducts);
      if (offerPrice < price) {
        res.push(o.id);
      }
    }
    return res;
  }, [
    isAdmin,
    downsellAllowed,
    planOffers,
    alreadySelectedProducts,
    duration,
    initialValues,
    supportOffers,
  ]);

  return (
    <CenteredModal
      title={t('editSession.upsell.title')}
      isOpen={isOpen && !!organizationId}
      onRequestClose={onRequestClose}
      xlarge
    >
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <Form>
          <h3 className={classes.title}>
            {t('studySetup.generalSetup.support.title')}
          </h3>
          <div className={classes.suportContainer}>
            {supportOffers.map((offer) => (
              <SupportOfferBlock
                key={offer.id}
                offer={offer}
                name="supportOfferId"
                alreadySelectedProducts={alreadySelectedProducts}
                noIllustration
                disabled={disabledOffers.includes(offer.id)}
              />
            ))}
          </div>
          <h3 className={classes.title}>
            {t('studySetup.generalSetup.option.title')}
          </h3>
          <div className={classes.optionsContainer}>
            <OfferOptionBlock
              offers={options}
              name="options"
              hideNotNeeded
              disabledOffers={disabledOffers}
            />
          </div>
          <div className={classes.buttons}>
            <SubmitButton key="submit" text={t('editSession.upsell.confirm')} />
          </div>
        </Form>
      </Formik>
    </CenteredModal>
  );
};
